export const enAdminTranslations = {
  admin: {
    header: {
      admin_dashboard_title: 'Admin Dashboard',
      admin_dashboard_subtitle: 'Let’s see how your facilities are performing',
      all: 'All',
      clear: 'Clear',
      downloadPdf: 'Download pdf',
    },
    tabs: {
      utilization: 'Utilization',
      fall_risk: 'Fall Risk',
      rtm: 'RTM',
      outcomes: 'Outcomes',
      new_marker: 'New',
    },
    today: {
      patients: 'Patients',
      workspaces: 'Workspaces',
      clinicians: 'Clinicians',
      measurements: 'Measurements',
      today_overview: 'Overview',
      workspaces_tooltip:
        '<bold>Number of valid workspaces included in the current selection.</bold>\n' +
        'Excludes test, training, and deactivated workspaces.\n' +
        'Includes workspaces even if no clinicians or patients are assigned yet.',
      clinicians_tooltip:
        '<bold>Number of clinician users in the selected workspaces.</bold>\n' +
        'Counts users with a <italic>Clinician</italic> or <italic>Admin</italic> role in at least one workspace, regardless of activity.\n' +
        'Excludes test/training users, workspace <italic>Members</italic>, org-level <italic>Admins</italic> and <italic>Auditors</italic>, and deactivated or removed users.',
      patients_tooltip:
        '<bold>Number of patients currently on the platform in the selected workspaces.</bold>\n' +
        'Excludes test, training, and discharged patients.\n' +
        'Patients are excluded once their status is marked as “discharged” in the system — whether manually or via EMR integration.\n' +
        'Care status or physical location does not affect whether a patient is counted.',
    },
    utilization: {
      test: 'test',
      test_plural: 'tests',
      tests: 'tests',
      walk: 'walk',
      walk_plural: 'walks',
      walks: 'walks',
      questionnaire: 'questionnaire',
      questionnaire_plural: 'questionnaires',
      questionnaires: 'questionnaires',
      utilization: 'Utilization',
      measurement_breakdown: 'Measurement Types',
      trend_over_time: 'Measurements by Month',
      measurement_by_workspace: 'Measurements by Workspace',
      clinician_by_measurement: 'Clinician-Performed Measurements',
      clinician_by_measurement_sub:
        'Measurements performed in the Clinician App (Walks & Tests only)',
    },
    rtm: {
      unit_toggle_tooltip: 'Number of codes or opportunities, not dollar values.',
      dollar_toggle_tooltip:
        'Displays estimated dollar value based on national average reimbursement rates.',
      98975: 'CPT 98975',
      98985: 'CPT 98985',
      98977: 'CPT 98977',
      98979: 'CPT 98979',
      98980: 'CPT 98980',
      98981: 'CPT 98981',
      rtm_billable_this_month: 'RTM Eligibility – This Month',
      rtm_billable_that_month: 'RTM Eligibility – {{date}}',
      rtm_billable_this_month_tooltip:
        'Number of times each RTM code met billing conditions during the current calendar month. All RTM codes are included — including 98975, 98985 and 98977, which are not calendar-month dependent but are grouped here for consistency.',
      rtm_monthly_trend: 'Eligibility – Monthly Trend',
      rtm_monthly_trend_tooltip:
        'Monthly trend of RTM code eligibility, based on completion of billing conditions. Chart reflects the date when conditions were met. Billing status and submission date are not tracked by the platform.',
      rtm_billable_by_workspace: 'RTM Eligibility – This Month by Workspace',
      rtm_eligibility_by_workspace_this_month: 'RTM Eligibility – This Month by Workspace',
      rtm_eligibility_by_workspace_that_month: 'RTM Eligibility – {{date}} by Workspace',
      rtm_eligibility_by_workspace_tooltip:
        'Breakdown of RTM code eligibility this month across selected workspaces. Reflects the number of times billing conditions were met for each code.',
      rtm_opportunities: 'Units',
      rtm_opportunities_this_month: 'Nearly Eligible – 98980/98981 Opportunities',
      rtm_opportunities_this_month_79: 'Nearly Eligible – 79/80/81 Opportunities',
      rtm_opportunities_that_month: 'Nearly Eligible – {{date}} 98980/98981 Opportunities',
      rtm_opportunities_that_month_79: 'Nearly Eligible – {{date}} 98979/98980/98981 Opportunities',
      rtm_opportunities_by_workspace_tooltip:
        'Counts patient opportunities that are close to qualifying for codes 98980 or 98981. Each case is missing fewer than 5 minutes of monitoring time, a required event, or both. Grouped by workspace.',
      rtm_opportunities_by_workspace_tooltip_79:
        'Counts patient opportunities that are close to qualifying for codes 98979, 98980 or 98981. Each case is missing fewer than 5 minutes of monitoring time, a required event, or both. Grouped by workspace.',
      rtm_opportunities_this_month_sub: 'Missing < 5 minutes or a monitoring event',
      eligible_rtm_codes: 'Units',
      clinicianMonitoring: 'Clinician monitoring time',
      patientActivity: 'Patient activity',
      education: 'Education',
    },
    fallrisk: {
      hide_no_assessment: 'Hide No Assessment',
      no_data_available: 'No data available',
      fall_risk: 'fall risk',
      low: 'low',
      medium: 'medium',
      high: 'high',
      Very_High: 'very high',
      Very_high: 'very high',
      very_high: 'very high',
      'no assessment': 'no assessment',
      no_assessment_tooltip_title: 'No assessment',
      no_assessment_tooltip_content:
        'Indicates patients who do not have recent enough data — or any data — to calculate fall risk.\n' +
        'Based on a platform-defined confidence threshold.',
      'high fall risk': 'high fall risk',
      measurement_breakdown: 'Current Fall Risk Levels',
      measurement_breakdown_sub: 'Data as of EOD {{date}}',
      measurement_breakdown_tooltip:
        'Fall risk levels are updated nightly and reflect the most recent full calculation.',
      over_time: 'Average Daily Fall Risk – by Month',
      over_time_tooltip:
        'Shows the average number of patients per day in each fall risk level, for each month.\n' +
        'Based on daily snapshots from the platform’s risk calculation.\n' +
        'Values are not patient-unique; they reflect overall volume and trend.',
      by_workspace: 'Current Fall Risk by Workspace',
      by_workspace_sub: 'Data as of EOD {{date}}',
      by_workspace_tooltip:
        'Fall risk levels are updated nightly and reflect the most recent full calculation.',
    },
    outcomes: {
      improved: 'improved',
      stable: 'stable',
      declined: 'declined',
      insufficient: 'insufficient data',
      timeline_label: 'Timeline',
      timeline_eval_to_discharge: 'Baseline → last',
      timeline_last_3_months: 'Last 3 months',
      timeline_last_month: 'Last month',
      hide_insufficient: 'Hide insufficient data',
      clear_filters: 'Clear Filters',
      discharged_patients: 'Discharged patients',
      patients_in_census: 'Patients in census',
      window_range: '{{range}}',
      measurable: 'With enough data',
      measurable_sub: '{{measure}}: {{min}}+ readings in the period · {{share}}% of patients',
      measurable_sub_without_measure: '{{min}}+ readings in the period · {{share}}% of patients',
      improved_of_measurable: 'Improved · of patients with enough data',
      declined_of_measurable: 'Declined · of patients with enough data',
      patients_count_one: '{{count}} patient',
      patients_count_other: '{{count}} patients',
      pts_vs_prior: '{{points}} pts vs prior',
      distribution_eval: 'Baseline → last outcomes',
      distribution_window: 'Outcome distribution · {{timeline}}',
      primary_outcome: 'Primary outcome',
      distribution_note:
        '{{measure}} · improved or declined means a change of at least one MCID ({{mcid}}); smaller changes are stable.',
      direction_note:
        '{{measure}}: improved means a lower score (less fall risk), declined means a higher score (more fall risk).',
      by_measure: 'Outcomes by measure',
      by_measure_sub: 'First vs last measurement in the period · MCID: {{note}}',
      no_data_yet: 'No data yet',
      measure_summary: '{{improved}}% improved · {{declined}}% declined · n={{measurable}}',
      frequency: 'Patient measurement frequency',
      frequency_sub: 'Measurements per patient in the period',
      frequency_note: 'Patients with fewer than {{min}} measurements cannot produce an outcome.',
      bucket_none: '0×',
      bucket_once: '1×',
      bucket_two_to_three: '2–3×',
      bucket_four_plus: '4+',
      vs_prior: '{{delta}} vs prior',
      clinicians: 'Clinician-performed measurements',
      clinicians_sub: 'Measurements performed on patients in the period',
      measurements_count_one: '{{count}} measurement',
      measurements_count_other: '{{count}} measurements',
      unique_patients_one: '{{count}} unique patient',
      unique_patients_other: '{{count}} unique patients',
      last_measured_one: 'last measured {{count}} day ago',
      last_measured_other: 'last measured {{count}} days ago',
      never_measured: 'no measurements yet',
      by_workspace: 'Outcomes by workspace',
      by_workspace_sub: 'Primary outcome ({{measure}}) per workspace',
      workspace_mode_label: 'Chart unit',
      mode_patients: 'Patients',
      mode_percent: '% with enough data',
      y_axis_patients: 'Number of patients',
      y_axis_percent: '% of patients with enough data',
      no_data_available: 'No data available',
      load_failed: 'Outcomes could not be loaded',
      close: 'Close',
      drilldown_hint: 'Click a chart to list the patients behind it',
      drilldown_disabled_hint: 'Patient drill-down is temporarily disabled',
      drilldown_count_one: '{{count}} patient',
      drilldown_count_other: '{{count}} patients',
      drilldown_any_verdict: 'all outcomes',
      drilldown_measure: '{{measure}} · {{verdict}}',
      drilldown_measurable: '{{measure}} · patients with enough data',
      drilldown_bucket: 'Measured {{bucket}} in the period',
      drilldown_clinician: 'Measured by {{name}}',
      drilldown_workspace: '{{workspace}} · {{verdict}}',
      drilldown_empty: 'No patients match',
      reading_change: '{{first}} → {{last}} {{unit}}',
      reading_single: '{{value}} {{unit}} · one reading',
      no_readings: 'No readings in the period',
      open_patient: 'Open the patient page of {{name}}',
      show_patients: 'Show patients',
    },
  },
};
